import { MessageResponseDto } from "@api/api";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { travelVoucherFormDefault } from "@factory/form/form.default";
import {
  InvoiceFormRule,
  InvoiceFormSchema,
  TravelVoucherFormRule,
  TravelVoucherFormSchema,
} from "@factory/form/form.rule";
import { useFormDefaultInvoice } from "@factory/form/form-default-invoice.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseInvoiceModel } from "@factory/model/query-model-invoice";
import { GetResponseQuotationModel } from "@factory/model/query-model-quotation";
import {
  useQyDeleteInvoice,
  useQyGetInvoiceById,
  useQyUpdateInvoice,
} from "@factory/query/invoice.query";
import { useQyGetQuotationById } from "@factory/query/quotation.query";
import { useQyCreateTravelVoucher } from "@factory/query/travel-voucher.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { AxiosError } from "axios";
import html2pdf from "html2pdf.js";
import { confirmDialog } from "primereact/confirmdialog";
import { useRef, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditInvoice() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const fileRef = useRef<HTMLDivElement>(null);
  const { getDefault } = useFormDefaultInvoice();
  const navigate = useNavigate();
  const { mapUpdateInvoice, mapDeleteInvoice, mapCreateTravelVoucher } =
    useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { invoiceId } = useParams();
  const [downloading, setDownloading] = useState(false)
  const actions: ButtonAction[] = [
    {
      label: "Create Travel Voucher",
      type: "split",
      value: ActionEnum.Approve,
    },
    {
      label: "Download",
      type: "more",
      value: ActionEnum.Download,
    },
    { label: "Update", type: "more", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Invoice
  const handleGetApiSuccess = (data: GetResponseInvoiceModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("baseQuote", responseData?.base_quote || "");
    setValue("invoiceDate", responseData?.invoice_date || "");
    setValue("dueDate", formatForDateInput(responseData?.due_date || ""));
    setValue("amount", responseData?.amount);
  };
  const {
    data: invoiceResponse,
    isLoading,
    isError: invoiceError,
  } = useQyGetInvoiceById(invoiceId || "", handleGetApiSuccess);
  const invoiceData = invoiceResponse?.data?.[0];

  const handleGetQuotationSuccess = (data: GetResponseQuotationModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }
    const responseData = data.data?.[0];
    setValue("quotation.basePackage", responseData?.base_package || "");
    setValue("quotation.packageName", responseData?.base_package_name || "");
    setValue("quotation.client", responseData?.client || "");
    setValue("quotation.clientName", responseData?.client_name || "");
    setValue("quotation.quotationDate", responseData?.quotation_date || "");
    setValue("quotation.location", responseData?.location || "");
    setValue("quotation.locationName", responseData?.location_name || "");
    setValue("quotation.startDate", formatDate(responseData?.start_date) || "");
    setValue("quotation.endDate", formatDate(responseData?.end_date) || "");
    setValue("quotation.numAdults", responseData?.num_adults);
    setValue("quotation.numChildren", responseData?.num_children);
    setValue("quotation.adultRateTotal", responseData?.adult_rate_total);
    setValue("quotation.childRateTotal", responseData?.child_rate_total);
    setValue("quotation.numNights", responseData?.num_nights);
    setValue("quotation.numDays", responseData?.num_days);
    setValue("quotation.agent", responseData?.agent || "");
    setValue("quotation.agentMarkup", responseData?.agent_markup);
    setValue("quotation.amount", responseData?.amount);
    setValue("quotation.packageInclusions", responseData?.package_inclusions);

    const transportItems = responseData?.transport?.map((item) => {
      const transportItem = {
        quotation: item.quotation,
        transportType: item.transport_type,
        transportTypeName: item.transport_type_name,
        fromLocation: item.from_location,
        fromLocationName: item.from_location_name,
        toLocation: item.to_location,
        toLocationName: item.to_location_name,
        pricePerPax: item.price_per_pax,
        numPeople: item.num_people,
        unit: item.unit,
        amount: item.amount,
      };
      return transportItem;
    });
    setValue("quotation.transport", transportItems || []);

    const accommodationItems = responseData?.accommodation?.map((item) => {
      const accommodationItem = {
        quotation: item.quotation,
        hotel: item.hotel,
        hotelName: item.hotel_name,
        roomType: item.room_type,
        roomTypeName: item.room_type_name,
        numPeople: item.num_people,
        unit: item.unit,
        validFrom: item.valid_from,
        validUntil: item.valid_until,
        name: item.name,
        price: item.price,
        withBreakfast: item.with_breakfast, // Added for UI purposes
      };
      return accommodationItem;
    });
    setValue("quotation.accommodation", accommodationItems || []);

    const activitiesItems = responseData?.activities?.map((item) => {
      const activitiesItem = {
        quotation: item.quotation,
        baseActivity: item.base_activity,
        baseActivityName: item.base_activity_name,
        isModified: item.is_modified,
        description: item.description,
        day: item.day,
        isChildActivity: item.is_child_activity,
        childRate: item.child_rate,
        showAsOptional: item.show_as_optional,
        isFree: item.is_free,
        entranceFee: item.entrance_fee,
        numPeople: item.num_people,
        unit: item.unit,
        name: item.name,
        ratePerPax: item.rate_per_pax,
        amount: item.amount,
      };
      return activitiesItem;
    });
    setValue("quotation.activities", activitiesItems || []);
  };
  useQyGetQuotationById(
    invoiceData?.base_quote || "",
    handleGetQuotationSuccess
  );

  // API UPDATE Invoice
  const handleUpdateApiSuccess = () => {
    showSuccess("Invoice updated successfully");
  };
  const { mutate: updateInvoice, isLoading: isEditLoading } =
    useQyUpdateInvoice(handleUpdateApiSuccess);

  // API DELETE Invoice
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Invoice deleted successfully");
  };
  const { mutate: deleteInvoice } = useQyDeleteInvoice(handleDeleteApiSuccess);

  // API CREATE TRAVEL VOUCHER
  const handleCreateVoucherSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;
    showSuccess(`New Travel Voucher is created`);
    navigate(`../../travel-voucher/${data.code}`);
  };
  const extractInvoiceUUID = (text: string): string | null => {
    const regex =
      /Voucher already exists: ([\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12})/i;
    const match = text.match(regex);
    if (match && match[1]) {
      return match[1];
    }
    return null;
  };
  const handleCreateVoucherError = (err: AxiosError) => {
    const voucherId = extractInvoiceUUID((err?.response?.data as any)?.message)
    if (voucherId !== null) {
      navigate(`../../travel-voucher/${voucherId}`);
    }
  };
  const { mutate: addTravelVoucher } = useQyCreateTravelVoucher(
    handleCreateVoucherSuccess,
    handleCreateVoucherError
  );

  // LOCAL FUNCTIONS
  const formatForDateInput = (dateValue: string | Date) => {
    if (!dateValue) return "";
    const date =
      dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (isNaN(date.getTime())) return ""; // invalid date
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  const formMethod = useForm<InvoiceFormSchema>({
    defaultValues: getDefault(invoiceData),
    resolver: zodResolver(InvoiceFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: InvoiceFormSchema) => {
    if (!invoiceData || !invoiceData.code) {
      throw new Error("No invoice data");
    }

    const formData = mapUpdateInvoice(form, invoiceData.code);
    updateInvoice(formData);
  };
  const handleValidateError = (err: FieldErrors<InvoiceFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!invoiceData || !invoiceData.code) {
      throw new Error("No invoice data found");
    }

    const form = mapDeleteInvoice(invoiceData.code);
    deleteInvoice(form);
  };

  // LOCAL Invoice Functions
  const travelVoucherFormMethod = useForm<TravelVoucherFormSchema>({
    defaultValues: travelVoucherFormDefault,
    resolver: zodResolver(TravelVoucherFormRule),
  });
  const { handleSubmit: handleSubmitTravelVoucher, setValue: setInvoiceValue } =
    travelVoucherFormMethod;

  const handleValidateTravelVoucher = (form: InvoiceFormSchema) => {
    const formData = mapCreateTravelVoucher(form);
    addTravelVoucher(formData);
  };
  const handleValidateTravelVoucherError = (
    err: FieldErrors<InvoiceFormSchema>
  ) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  const handleAction = (action: ButtonAction) => {
    const date = new Date();
    switch (action.value) {
      case ActionEnum.Approve:
        setInvoiceValue("baseInvoice", invoiceData?.code);
        setInvoiceValue(
          "confirmationNum",
          `HTBV${date.getFullYear()}${date.getMonth()}${date.getDay()}${date.getHours()}${date.getMinutes()}${date.getSeconds()}`
        );
        setInvoiceValue("paymentCompletion", 0);
        setInvoiceValue("isFullyPaid", false);
        setInvoiceValue("amountPaid", 0);
        setInvoiceValue("amount", 0);
        handleSubmitTravelVoucher(
          handleValidateTravelVoucher,
          handleValidateTravelVoucherError
        )();
        break;
      case ActionEnum.Download:
        setDownloading(true)
        downloadPdf(`Invoice-${invoiceData?.code}.pdf`);
        setDownloading(false)
        break;

      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  const downloadPdf = (fileName: string) => {
    if (!fileRef.current) return;

    const opt = {
      margin: 0.1,
      filename: fileName,
      image: { type: "jpeg", quality: 1 },
      html2canvas: {
        scale: 2, // Increase scale for better quality
        useCORS: true, // Enable CORS
        allowTaint: true, // Allow cross-origin images
        logging: true, // Enable logging for debugging
        imageTimeout: 15000, // Increase timeout
      },
      jsPDF: {
        orientation: "portrait",
        unit: "pt", // use points
        format: "legal",
        // format: [pdfWidth, pdfHeight],
      },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    html2pdf().set(opt).from(fileRef.current).save();
  };

  return {
    invoiceId,
    invoiceData,
    isLoading,
    invoiceError,
    formMethod,
    actions,
    isEditLoading,
    fileRef,
    downloading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
